import { ActionIcon, Portal, Tooltip } from '@mantine/core';
import { Map, Satellite } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';

import styles from './style-control.module.css';

const MAP_STYLES = {
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
} as const;

type MapStyleKey = keyof typeof MAP_STYLES;

/* eslint-disable-next-line */
export interface StyleControlProps {
  position?: 'top-left' | 'top-right';
}

export function StyleControl({ position = 'top-left' }: StyleControlProps) {
  const { t } = useTranslation();
  const { current: map } = useMap();
  const mapId = map?.getMap().getContainer().id;
  const [styleKey, setStyleKey] = useState<MapStyleKey>('satellite');

  const handleToggle = () => {
    const nextStyleKey: MapStyleKey =
      styleKey === 'satellite' ? 'outdoors' : 'satellite';
    setStyleKey(nextStyleKey);
    map?.getMap().setStyle(MAP_STYLES[nextStyleKey]);
  };

  if (!mapId) {
    return null;
  }

  const isSatellite = styleKey === 'satellite';
  const label = isSatellite
    ? t('map.style-control.switchToOutdoors')
    : t('map.style-control.switchToSatellite');

  return (
    <Portal
      target={
        document &&
        (document
          .getElementById(mapId)
          ?.getElementsByClassName(`mapboxgl-ctrl-${position}`)[0] as HTMLElement)
      }
    >
      <div className={`${styles.container} mapboxgl-ctrl mapboxgl-ctrl-group`}>
        <Tooltip label={label} position="left" withArrow>
          <ActionIcon
            aria-label={label}
            className={styles.button}
            onClick={handleToggle}
            radius="sm"
            size={29}
            variant="default"
          >
            {isSatellite ? (
              <Map color="currentColor" size={18} />
            ) : (
              <Satellite color="currentColor" size={18} />
            )}
          </ActionIcon>
        </Tooltip>
      </div>
    </Portal>
  );
}

export default StyleControl;
